import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  onScrollableChange?: (state: {
    vertical: boolean
    horizontal: boolean
  }) => void
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, onScrollableChange, ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport || !onScrollableChange) return

    const checkOverflow = () => {
      const needsVerticalScroll = viewport.scrollHeight > viewport.offsetHeight
      const needsHorizontalScroll = viewport.scrollWidth > viewport.offsetWidth

      onScrollableChange({
        vertical: needsVerticalScroll,
        horizontal: needsHorizontalScroll,
      })
    }

    // Check immediately
    checkOverflow()

    // Observe viewport size changes
    const resizeObserver = new ResizeObserver(checkOverflow)
    resizeObserver.observe(viewport)

    // Observe content size changes (the inner div)
    const contentElement = viewport.querySelector('[style*="display: table"]')
    if (contentElement) {
      resizeObserver.observe(contentElement)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        className="h-full w-full rounded-[inherit] [&>div]:!block"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
})

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

const ScrollAreaWithShadow = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => {
  const [scrollInfo, setScrollInfo] = React.useState({
    vertical: false,
    horizontal: false,
  })

  return (
    <ScrollArea
      className={`${className} ${scrollInfo.vertical ? "[&_tfoot]:shadow-[0_-10px_10px_-10px_rgba(0,0,0,0.5)] dark:[&_tfoot]:shadow-[0_-10px_10px_-10px_rgba(255,255,255,0.15)]" : ""}`}
      {...props}
      onScrollableChange={setScrollInfo}
    >
      {children}
    </ScrollArea>
  )
})

export { ScrollArea, ScrollBar, ScrollAreaWithShadow }
